import { PropsWithChildren, createContext, useContext, useEffect } from "react";

import { useRouter } from "next/router";

import { useProBuyerProjectsAccess } from "@/Apollo/hooks/useProBuyerProjects";
import { ProBuyerProjectsAccess } from "@/Apollo/schema";
import { useAuth } from "@/auth";
import { PRO_BUYER_ROUTE } from "@/features/pro-buyer-application/static/pro-buyer-application.static";
import { PRO_BUYER_PROJECTS_UI_GATE } from "@/features/pro-buyer-projects/static/pro-buyer-projects.static";
import { useFeatureGate } from "@/features/statsig/gate.hooks";

/**
 * WHO-5959 6b, spec S1. FULL for an approved Pro Buyer; READ_ONLY for a Pro
 * Buyer switched off by CS who still has projects (look and download only);
 * NONE for everyone else, who is sent to /pro-buyer.
 */
export const useCanUseProBuyerProjects = () => {
  const { isLoggedIn } = useAuth();
  /* Statsig `pro_buyer_projects_ui`: off or still loading means no access, so nothing flashes. */
  const { isEnabled: gateOn, isLoading: gateLoading } = useFeatureGate(
    PRO_BUYER_PROJECTS_UI_GATE
  );
  const { data, loading } = useProBuyerProjectsAccess({
    skip: !isLoggedIn || !gateOn,
  });
  const gatewayAccess = data?.me?.proBuyerProjectsAccess ?? null;
  /* A settled "off" gate behaves exactly like a gateway NONE (S1 redirect included). */
  const gateDenied = isLoggedIn && !gateOn && !gateLoading;
  const access = gateOn
    ? gatewayAccess
    : gateDenied
      ? ProBuyerProjectsAccess.None
      : null;

  return {
    access,
    memberId: gateOn ? (data?.me?.id ?? null) : null,
    canUse:
      access === ProBuyerProjectsAccess.Full ||
      access === ProBuyerProjectsAccess.ReadOnly,
    readOnly: access === ProBuyerProjectsAccess.ReadOnly,
    loading: isLoggedIn && (gateLoading || (gateOn && loading && !data)),
  };
};

const ReadOnlyContext = createContext(false);

/** True inside the tab for a READ_ONLY viewer: hide every create, add, rename and delete control. */
export const useProProjectsReadOnly = () => useContext(ReadOnlyContext);

/** Renders the tab only for a viewer who may use it; everyone else goes to /pro-buyer. */
export const ProProjectsGate = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const { access, canUse, readOnly, loading } = useCanUseProBuyerProjects();
  const denied = !loading && access !== null && !canUse;

  useEffect(() => {
    if (denied) {
      void router.replace(PRO_BUYER_ROUTE);
    }
  }, [denied, router]);

  if (!canUse) {
    return null;
  }

  return (
    <ReadOnlyContext.Provider value={readOnly}>
      {children}
    </ReadOnlyContext.Provider>
  );
};
